import { Alert, Box, Button, CircularProgress } from "@mui/material";
import { TabLinks } from "components/TabLinks";
import { useOmnisearchActions, useOmnisearchStore } from "hooks/omnisearch-v2";
import { useTenant } from "hooks/useTenant";
import { useEffect } from "react";
import { Outlet, useLocation, useMatch } from "react-router-dom";
import { addOrUpdateKeyInOmnisearch } from "utils/omnisearchUtils";

import { InvoiceTrackingDateFilter } from "./InvoiceTrackingDateFilter";
import {
  normalizeInvoiceTrackingSearch,
  PERIOD_KEY,
  periodError,
  periodFromSearch,
  periodSearch,
  useInvoiceTrackingPeriod,
} from "./invoiceTrackingPeriod";

export function WasteInvoicesLayout() {
  const { tenantId } = useTenant();
  const { pathname } = useLocation();
  const base = `/o/${tenantId}/waste/invoices`;
  const auditMatch = useMatch({ path: `${base}/audit`, end: false });
  const scheduleMatch = useMatch({ path: `${base}/schedule-adherence`, end: false });
  // Every match is read on every render. Folding these into one `||` expression
  // short-circuits, which skips the later hooks on the routes the earlier ones
  // match and changes how many this component calls from route to route.
  const invoicesExact = useMatch({ path: base, end: true });
  const auditExact = useMatch({ path: `${base}/audit`, end: true });
  const scheduleExact = useMatch({ path: `${base}/schedule-adherence`, end: true });
  // A period scopes a list; one invoice is not a period. Shown on the three
  // lists and nowhere else, so a detail page cannot leave a reader looking at a
  // range that has nothing to do with what is on screen.
  const onList = invoicesExact != null || auditExact != null || scheduleExact != null;
  const { period, setPeriod, ready, error, retry } = useInvoiceTrackingPeriod(onList);
  const { omnisearch } = useOmnisearchStore();
  const { setOmnisearchImmediate } = useOmnisearchActions();
  const normalizedSearch = normalizeInvoiceTrackingSearch(omnisearch);
  const hasStatedPeriod = Boolean(periodFromSearch(normalizedSearch).from);
  const invalidPeriod = periodError(period);
  const searchWithPeriod =
    ready && !hasStatedPeriod
      ? addOrUpdateKeyInOmnisearch(
          normalizedSearch,
          PERIOD_KEY,
          `${period.from}..${period.through}`
        )
      : normalizedSearch;
  useEffect(() => {
    // The URL store can notify before the router catches up during navigation.
    if (
      onList &&
      window.location.pathname === pathname &&
      ready &&
      searchWithPeriod !== omnisearch
    ) {
      setOmnisearchImmediate(searchWithPeriod);
    }
  }, [onList, pathname, ready, searchWithPeriod, omnisearch, setOmnisearchImmediate]);
  const search = periodSearch(period);
  // A plain chain rather than nested ternaries: the tabs are a list, and a
  // fourth would otherwise nest one deeper again.
  const activeTab = () => {
    if (auditMatch) return `${base}/audit${search}`;
    if (scheduleMatch) return `${base}/schedule-adherence${search}`;
    return `${base}${search}`;
  };
  return (
    <Box>
      <TabLinks
        // Three long labels overflow a phone: without this the last tab is cut
        // off at the edge with nothing to scroll it into view.
        scrollable
        value={activeTab()}
        tabs={[
          { title: "Invoices", to: `${base}${search}` },
          { title: "Audit", to: `${base}/audit${search}` },
          { title: "Schedule Adherence", to: `${base}/schedule-adherence${search}` },
        ]}
        aria-label="invoice tabs"
        role="navigation"
      />
      {onList ? (
        <InvoiceTrackingDateFilter
          from={period.from}
          through={period.through}
          onChange={setPeriod}
          errorMessage={invalidPeriod}
          dateBasis={scheduleMatch ? "service" : "invoice"}
        />
      ) : null}
      {onList && error ? (
        <Alert severity="error" action={<Button onClick={retry}>Retry</Button>}>
          Unable to load the default date range.
        </Alert>
      ) : onList && (!ready || searchWithPeriod !== omnisearch) ? (
        <Box sx={{ p: 3 }}>
          <CircularProgress aria-label="Loading invoice date range" />
        </Box>
      ) : onList && invalidPeriod ? null : (
        <Outlet />
      )}
    </Box>
  );
}
